import * as ko from "knockout";
import * as Rx from "rxjs";

import pliCall, { PliCallResponse, PliCallSettings } from "../core/call";

/**
 * Settings for rx utils that wrap pliCall.
 */
export interface PliCallRxSettings<TRequestData> extends PliCallSettings<TRequestData, any> {
    /**
     * When true, failed calls will be returned with successful results; when false, failed calls will terminate the stream with an error.
     *
     * @default false
     */
    includeErrorsInResultStream?: boolean;
}

/**
 * Returns an Rx.Observable that signals when the underlying ko.observable changes.
 *
 * @param source
 *     Knockout observable or computed.
 * @param omitInitialValue
 *     When true, the initial value will not be published on the event stream. @default false.
 * @returns
 *     A cold Rx Observable stream containing changes to the source observable.
 */
export function fromKnockoutObservable<T>(source: (ko.Observable<T> | ko.Computed<T>), omitInitialValue: boolean = false): Rx.Observable<T> {
    if (!ko.isObservable(source) && !ko.isComputed(source)) { throw new Error("'source' must be a knockout observable."); }
    return new Rx.Observable<T>(function createRxStreamForKnockoutObservable(observer: Rx.Subscriber<T>): Rx.Subscription {
        if (!omitInitialValue) {
            // publish initial value
            observer.next(source.peek());
        }

        // publish future values (and define how to cancel publishing)
        const koSubscription = source.subscribe(observer.next, observer);
        const rxSubscription = observer.add(koSubscription.dispose.bind(koSubscription));

        // if the specified Knockout observable is disposable,
        // override disposal to complete Rx stream in addition
        // to the original disposal
        const originalDispose = (<any>source).dispose;
        if (typeof originalDispose === "function") {
            (<any>source).dispose = function disposeRxStreamForKnockoutObservable(): void {
                originalDispose.call(this);
                observer.complete(); // "OnComplete" event + disposes (unsubscribes) all subscriptions, including "rxSubscription" which in turn disposes "koSubscription"
            };
        }

        // allow caller to cancel publishing of future values
        return rxSubscription;
    });
}

/**
 * Returns an Rx.Observable that signals when the underlying PLI call completes.
 *
 * @param settings
 *     PliCallSettings object containing the PLI call settings.
 * @returns
 *     A cold Rx Observable stream containing the PLI call result.
 */
export function fromPliCall<TResponseData>(settings: PliCallRxSettings<any>): Rx.Observable<PliCallResponse<TResponseData, any>> {
    return new Rx.Observable<PliCallResponse<TResponseData, any>>(
        function createRxStreamForPliCall(observer: Rx.Subscriber<PliCallResponse<TResponseData, any>>): Rx.Subscription {
            const promise = pliCall<any, any, TResponseData>(settings);
            const rxSubscription = observer.add(function disposeRxStreamForPliCall(): void {
                // try to abort any in-flight requests if
                // 'unsubscribe' is called before completion
                if (promise.state && promise.state() === "pending" && promise.xhr && promise.xhr.abort) {
                    promise.xhr.abort();
                }
            });

            promise.always(function notifyRxStreamForPliCall(response: PliCallResponse<TResponseData, any>): void {
                if (!rxSubscription.closed) {
                    if (response.isError && !settings.includeErrorsInResultStream) {
                        observer.error(response);
                    } else {
                        observer.next(response);
                        observer.complete();
                    }
                }
            });

            return rxSubscription;
        }
    );
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/rx.ts